import { useContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { menuItemPaddingLeft } from '@/shared/components/export';
import {
  ViewMode,
  useWidgetViewMode,
  DEFAULT_TEXT_SCALE,
  MIN_TEXT_SCALE,
  MAX_TEXT_SCALE,
} from '@/core/widgets/contexts/widget-view-mode-context';
import { WidgetContainerContext } from '@/core/widgets/contexts/widget-container-context';
import { authService, useSessionId } from '@/core/auth/export';
import {
  analyticsService,
  AnalyticsEvent,
  AnalyticsSubTypeEvent,
  AnalyticsTypes,
} from '@/shared/analytics/export';

const VIEW_MODE_OPTIONS: { value: ViewMode; label: string }[] = [
  { value: 'normal', label: 'Normal view' },
  { value: 'headline', label: 'Headline view' },
  { value: 'readMore', label: 'Read more view' },
];

/** View-mode + text-size controls, shared between the standalone dropdown and the widget's ellipsis menu. */
export default function ViewModeMenuItems({
  onSelect,
  depth = 0,
}: {
  onSelect?: () => void;
  /** Nesting depth this content renders at (matches MenuListRow's indent formula) — pass 1 when nested under a collapsible "View" row. */
  depth?: number;
}) {
  const { viewMode, setViewMode, textScale, setTextScale } = useWidgetViewMode();
  const { widgetId } = useContext(WidgetContainerContext);
  const sessionId = useSessionId();

  const handleSelect = (value: ViewMode) => {
    setViewMode(value);
    analyticsService.recordAnalyticsEvent(AnalyticsEvent.WIDGET_CONFIG_CHANGED, {
      subject: authService.getSubjectData(),
      object: {
        type: AnalyticsTypes.WIDGET,
        id: widgetId,
        eventSubType: AnalyticsSubTypeEvent.WIDGET_VIEW_MODE_CHANGED,
        viewMode: value,
      },
      session: { id: sessionId },
    });
    onSelect?.();
  };

  const handleTextScaleChange = (value: number) => {
    setTextScale(value);
    analyticsService.recordAnalyticsEvent(AnalyticsEvent.WIDGET_CONFIG_CHANGED, {
      subject: authService.getSubjectData(),
      object: {
        type: AnalyticsTypes.WIDGET,
        id: widgetId,
        eventSubType: AnalyticsSubTypeEvent.WIDGET_VIEW_MODE_CHANGED,
        textScale: value,
      },
      session: { id: sessionId },
    });
  };

  return (
    <>
      {VIEW_MODE_OPTIONS.map(option => (
        <div
          key={option.value}
          role={'button'}
          tabIndex={0}
          className={'menu-list-item'}
          style={{ paddingLeft: menuItemPaddingLeft(depth) }}
          onClick={() => handleSelect(option.value)}
          onKeyDown={event => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              handleSelect(option.value);
            }
          }}
        >
          <span className={'menu-list-item-label'}>{option.label}</span>
          {viewMode === option.value && (
            <FontAwesomeIcon icon={faCheck} className={'menu-list-item-check'} />
          )}
        </div>
      ))}
      <div
        className={'pe-3 py-1'}
        style={{ paddingLeft: menuItemPaddingLeft(depth) }}
        onClick={e => e.stopPropagation()}
      >
        <div className={'d-flex justify-content-between align-items-center mb-1'}>
          <span className={'menu-list-item-label'}>Text size</span>
          {Math.round(textScale * 100) !== Math.round(DEFAULT_TEXT_SCALE * 100) && (
            <button
              className={'btn btn-link btn-sm p-0 text-decoration-none'}
              onClick={() => handleTextScaleChange(DEFAULT_TEXT_SCALE)}
            >
              Reset
            </button>
          )}
        </div>
        <div className={'menu-list-text-size d-flex align-items-center gap-2'}>
          <small className={'menu-list-text-size-icon'}>A</small>
          <input
            type={'range'}
            className={'form-range menu-list-text-size-range'}
            min={Math.round(MIN_TEXT_SCALE * 100)}
            max={Math.round(MAX_TEXT_SCALE * 100)}
            step={5}
            value={Math.round(textScale * 100)}
            onChange={e => handleTextScaleChange(parseInt(e.target.value, 10) / 100)}
          />
          <span className={'fs-5 menu-list-text-size-icon lh-1'}>A</span>
        </div>
      </div>
    </>
  );
}
